<template>
  <div class="has-tab-bar">
    <app-bar
      title=""
      :show-back-button="true"
      back-button-path="/profile-own"
    />
    <spinner-loader v-if="loading" />
    <div class="container">
      <h1>Mijn strippenkaarten</h1>
      <tab-navigation
        v-if="showTabNavigation"
        :tabs="tabs"
        :active-tab="activeTab"
        class="mb-8"
        @toggle="changeTab"
      />
      <template v-if="activeTab === CREATED_CARDS">
        <!-- Filter en sorteer controls -->
        <div
          v-if="createdCards.length"
          class="flex flex-wrap gap-3 mb-4 pb-4 border-b border-gray-200"
        >
          <div class="flex items-center gap-2">
            <label class="text-sm text-gray-600">Sorteer:</label>
            <select
              v-model="sortField"
              class="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white"
            >
              <option
                v-for="option in sortOptions"
                :key="option.key"
                :value="option.key"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <toggle
            :value="showOnlyActive"
            title="Alleen actief"
            @toggle="showOnlyActive = $event"
          />
        </div>

        <div v-if="filteredAndSortedCards.length" class="space-y-4 mb-8">
          <creditcard-card
            v-for="card in filteredAndSortedCards"
            :key="card.id"
            :card="{ ...card, isOwner: true }"
            :show-buy-button="false"
            @click="gotoCard(card.id)"
          />
        </div>
        <div v-else-if="createdCards.length" class="mb-8 text-gray-500">
          Geen strippenkaarten gevonden met de huidige filters.
        </div>
        <div v-else class="mb-8">
          Je hebt nog geen strippenkaarten aangemaakt. Klik op de knop hieronder
          om een nieuwe strippenkaart aan te maken
        </div>
        <button class="button button-primary w-full" @click="routeToCreateCard">
          Maak nieuwe strippenkaart
        </button>
      </template>
      <template v-else-if="activeTab === PURCHASED_CARDS">
        <div v-if="purchasedCards.length" class="space-y-4 mb-8">
          <creditcard-card
            v-for="card in purchasedCards"
            :key="card.id"
            :card="card"
            :show-buy-button="false"
            :show-description="false"
            :show-credits="true"
            @click="gotoCard(card.id)"
          />
        </div>
        <div v-else class="mb-8">
          Je hebt nog geen strippenkaarten gekocht. Deze kun je kopen bij
          activiteiten waarbij je gebruik kunt maken van een strippenkaart.
        </div>
        <button
          v-if="activeTab === CREATED_CARDS"
          class="button button-primary w-full"
          @click="routeToCreateCard"
        >
          Maak nieuwe strippenkaart
        </button>
      </template>
    </div>
    <tab-bar selected="" />
  </div>
</template>

<script>
import AppBar from '@/components/AppBar'
import SpinnerLoader from '@/components/SpinnerLoader'
import TabBar from '@/components/TabBar'
import Toggle from '@/components/Toggle'
import { mapGetters, mapState } from 'vuex'
import { CREDITCARD_GET_ALL } from '../store/actions/creditcard'
import CreditcardCard from '@/components/CreditcardCard'
import TabNavigation from '@/components/TabNavigation'
import { PARTICIPANT_GET_CREDITCARDS } from '../store/actions/participant'

const CREATED_CARDS = 'CREATED_CARDS'
const PURCHASED_CARDS = 'PURCHASED_CARDS'

export default {
  name: 'ProfileCards',
  components: {
    AppBar,
    SpinnerLoader,
    TabBar,
    Toggle,
    CreditcardCard,
    TabNavigation
  },
  data() {
    return {
      loading: false,
      showTabNavigation: true,
      tabs: [
        { key: PURCHASED_CARDS, value: 'Gekocht' },
        { key: CREATED_CARDS, value: 'Aangemaakt' }
      ],
      CREATED_CARDS: CREATED_CARDS,
      PURCHASED_CARDS: PURCHASED_CARDS,
      activeTab: PURCHASED_CARDS,
      showOnlyActive: true,
      sortField: 'created_desc',
      sortOptions: [
        { key: 'created_desc', label: 'Nieuwste eerst' },
        { key: 'created_asc', label: 'Oudste eerst' },
        { key: 'name_asc', label: 'Naam (A-Z)' },
        { key: 'name_desc', label: 'Naam (Z-A)' },
        { key: 'cost_asc', label: 'Prijs (laag-hoog)' },
        { key: 'cost_desc', label: 'Prijs (hoog-laag)' },
        { key: 'purchases_desc', label: 'Meest gekocht' }
      ]
    }
  },
  computed: {
    ...mapGetters(['authId']),
    ...mapState({
      createdCards: (state) => state.creditcard.all,
      purchasedCards: (state) => state.participant.purchasedCreditcards
    }),
    filteredAndSortedCards() {
      if (!this.createdCards) return []

      let cards = [...this.createdCards]

      // Filter op actief/inactief
      if (this.showOnlyActive) {
        cards = cards.filter((card) => card.active)
      }

      // Sorteer
      cards.sort((a, b) => {
        switch (this.sortField) {
          case 'created_desc':
            return new Date(b.created || 0) - new Date(a.created || 0)
          case 'created_asc':
            return new Date(a.created || 0) - new Date(b.created || 0)
          case 'name_asc':
            return (a.name || '').localeCompare(b.name || '')
          case 'name_desc':
            return (b.name || '').localeCompare(a.name || '')
          case 'cost_asc':
            return (a.cost || 0) - (b.cost || 0)
          case 'cost_desc':
            return (b.cost || 0) - (a.cost || 0)
          case 'purchases_desc':
            return (b.purchased_by?.length || 0) - (a.purchased_by?.length || 0)
          default:
            return 0
        }
      })

      return cards
    }
  },
  watch: {},
  created: function () {
    this.$store.dispatch(CREDITCARD_GET_ALL, { ownerId: parseInt(this.authId) })
    this.$store.dispatch(PARTICIPANT_GET_CREDITCARDS)
  },
  methods: {
    changeTab(target) {
      this.activeTab = target
    },
    routeToCreateCard() {
      this.$router.push('/cards/new')
    },
    gotoCard(cardId) {
      this.$router.push(`/cards/${cardId}`)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
</style>
